import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationExtras, Router } from '@angular/router';
import { RouteState } from '../models/route-state';
import { GlobalConstants } from '../constants/global-constants.constant';
import { LocalStorageService } from './local-storage.service';
import { environment } from 'src/environments/environment';
import { isLaasPortalEmbeddedInParentFrame } from '../utils/embedding.util';

/**
 * Route state service
 * Save all route data, helps to navigate routes
 */
@Injectable({
  providedIn: 'root',
})
export class RouteStateService {

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private location: Location) {

  }

  /**
   * get current route data
   */
  getCurrent(): RouteState {
    const path = this.getPathWithoutQuery(this.router.url);
    const routeStates = this.getRouteStates(path);
    return routeStates[routeStates.length - 1];
  }

  /**
   * Get current route data and remove it from storage so it cannot be read again
   * (e.g. after page refresh).
   */
  getCurrentAndConsume(): RouteState | undefined {
    const path = this.getPathWithoutQuery(this.router.url);
    const allRouteStates = this.getAllRouteStates();

    for (let i = allRouteStates.length - 1; i >= 0; i--) {
      if (allRouteStates[i].path !== path) {
        continue;
      }

      const [current] = allRouteStates.splice(i, 1);
      this.saveRouteStates(allRouteStates);
      return current;
    }

    return undefined;
  }

  getCurrentUrl(): string {
    return this.router.url;
  }

  /**
   * add route data
   * @param title route name
   * @param path route path
   * @param data route data
   * @param isCleanAll is parent route
   */
  navigateTo(path: string, data?: any, isNewTab?: boolean, isCleanAll?: boolean): void {
    if (isCleanAll) {
      this.clear();
    }

    const routeStates = this.getAllRouteStates();
    const time = new Date().getTime();
    const routeState = { path: this.getPathWithoutQuery(path), data, time };
    routeStates.push(routeState);
    this.saveRouteStates(routeStates);
    if (isNewTab) {
      this.openInNewTab(path);
    } else {
      this.router.navigate([`/${path}`]);
    }
  }

  /**
   * 嵌入在新系統內時，另開分頁應開「新系統外框 + legacyPath 內嵌舊路徑」；
   * 需設定 environment.laasPlatformBaseUrl；未設定則回退為同網域 path。
   */
  private openInNewTab(path: string): void {
    const base = environment.laasPlatformBaseUrl?.replace(/\/+$/, '');
    const isAbsoluteHttpUrl = /^https?:\/\//i.test(path);

    if (isAbsoluteHttpUrl) {
      globalThis.open(path, '_blank', 'noopener');
      return;
    }

    if (isLaasPortalEmbeddedInParentFrame() && base) {
      const pluginPath = environment.laasLegacyFramePluginPath ?? 'legacyMenu';
      const legacyPath = path.startsWith('/') ? path : `/${path}`;
      const directUrl = `${base}/${pluginPath}/frame/direct?legacyPath=${encodeURIComponent(legacyPath)}`;
      globalThis.open(directUrl, '_blank', 'noopener');
      return;
    }

    globalThis.open(path, '_blank', 'noopener');
  }

  /**
   * load previous route
   */
  loadPrevious(): void {
    const routeStates = this.getAllRouteStates();
    routeStates.pop();
    this.saveRouteStates(routeStates);
    // const routeState = routeStates[routeStates.length - 1];
    // this.router.navigate([routeState.path]);
    this.location.back();
  }

  clear(): void {
    this.localStorageService.removeItem(GlobalConstants.routeStateKey);
  }

  remove(path: string): void {
    const normalizedPath = this.getPathWithoutQuery(path);
    const allRouteStates = this.getAllRouteStates();
    const routeStates = allRouteStates.filter(x => x.path !== normalizedPath);
    this.saveRouteStates(routeStates);
  }

  private saveRouteStates(routeStates: RouteState[]): void {
    this.localStorageService.setItem(GlobalConstants.routeStateKey, routeStates);
  }

  private getAllRouteStates(): RouteState[] {
    const routeStates: RouteState[] = this.localStorageService.getItem(GlobalConstants.routeStateKey) ?? [];
    return routeStates;
  }

  private getRouteStates(path: string): RouteState[] {
    const normalizedPath = this.getPathWithoutQuery(path);
    const allRouteStates = this.getAllRouteStates();
    return allRouteStates.filter(x => x.path === normalizedPath);
  }

  updateData(path: string, data: any) {
    this.remove(path);
    const routeStates = this.getAllRouteStates();
    const time = new Date().getTime();
    const routeState = { path: this.getPathWithoutQuery(path), data, time };
    routeStates.push(routeState);
    this.saveRouteStates(routeStates);

    const queryParams = data;
    const navigationExtras: NavigationExtras = { queryParams };
    this.router.navigate([`/${path}`], navigationExtras);
  }

  private getPathWithoutQuery(path: string): string {
    try {
      return this.router.parseUrl(path).toString().split('?')[0];
    } catch {
      return path.split('?')[0];
    }
  }
}
